import React, { useState, useEffect } from 'react';
import { useWindowWidth } from '../lib/useWindowWidth';

const FIPS_TO_NAME = {
    '01': 'Alabama', '02': 'Alaska', '04': 'Arizona', '05': 'Arkansas', '06': 'California',
    '08': 'Colorado', '09': 'Connecticut', '10': 'Delaware', '11': 'District of Columbia', '12': 'Florida',
    '13': 'Georgia', '15': 'Hawaii', '16': 'Idaho', '17': 'Illinois', '18': 'Indiana',
    '19': 'Iowa', '20': 'Kansas', '21': 'Kentucky', '22': 'Louisiana', '23': 'Maine',
    '24': 'Maryland', '25': 'Massachusetts', '26': 'Michigan', '27': 'Minnesota', '28': 'Mississippi',
    '29': 'Missouri', '30': 'Montana', '31': 'Nebraska', '32': 'Nevada', '33': 'New Hampshire',
    '34': 'New Jersey', '35': 'New Mexico', '36': 'New York', '37': 'North Carolina', '38': 'North Dakota',
    '39': 'Ohio', '40': 'Oklahoma', '41': 'Oregon', '42': 'Pennsylvania', '44': 'Rhode Island',
    '45': 'South Carolina', '46': 'South Dakota', '47': 'Tennessee', '48': 'Texas', '49': 'Utah',
    '50': 'Vermont', '51': 'Virginia', '53': 'Washington', '54': 'West Virginia', '55': 'Wisconsin',
    '56': 'Wyoming', '72': 'Puerto Rico'
};

const TopStatsPanel = ({ 
     predictionData, 
     selectedDay, 
     countiesList, 
     currentMetric = 'mean',
     signupTopStatesPercent = [],
     signupTopCountiesOut = [],
     signupTopCountiesPercent = []
 }) => {
    const [countyTotals, setCountyTotals] = useState(null);
    const [stateTotals, setStateTotals] = useState(null);
    const [activeMobileStat, setActiveMobileStat] = useState('topStatesOut');
    const isMobileViewport = useWindowWidth() < 992;
    const [processedStats, setProcessedStats] = useState({
        topStatesOut: [],
        topStatesPercent: [],
        topCountiesOut: [],
        topCountiesPercent: []
    });

    // Fetch totals JSONs
    useEffect(() => {
        const fetchTotals = async () => {
            try {
                const [cRes, sRes] = await Promise.all([
                    fetch('/data/countytotal.json'),
                    fetch('/data/statetotal.json')
                ]);
                const cData = await cRes.json();
                const sData = await sRes.json();
                setCountyTotals(cData);
                setStateTotals(sData);
            } catch (err) {
                console.error("Error fetching total data:", err);
            }
        };
        fetchTotals();
    }, []);

    // Process data for rankings
    useEffect(() => {
        if (!predictionData || !selectedDay || !countyTotals || !stateTotals || !countiesList) return;

        // 1. Filter prediction data for selected date -> { countyFips: customersOut }
        const countyOuts = {};
        const stateOuts = {}; // { stateFips: totalOut }

        Object.entries(predictionData).forEach(([fips, days]) => {
            // Find the day
            const dayData = days.find(d => d.date.startsWith(selectedDay));
            if (dayData && dayData.customers) {
                // Use currentMetric (default mean)
                const val = dayData.customers[currentMetric] || 0;

                // If FIPS is 2 digits, it's already a state FIPS
                const fipsStr = fips.toString();
                if (fipsStr.length <= 2) {
                    const stateFips = fipsStr.padStart(2, '0');
                    stateOuts[stateFips] = (stateOuts[stateFips] || 0) + val;
                } else {
                    // It's a county FIPS
                    countyOuts[fips] = val;
                    const paddedFips = fipsStr.padStart(5, '0');
                    const stateFips = paddedFips.substring(0, 2);
                    stateOuts[stateFips] = (stateOuts[stateFips] || 0) + val;
                }
            }
        });


        // 2. Rankings

        // Top 10 States Out
        const topStatesOut = Object.entries(stateOuts)
            .map(([fips, val]) => ({
                id: fips,
                name: FIPS_TO_NAME[fips] || fips,
                val: val,
                formattedVal: Math.round(val).toLocaleString()
            }))
            .sort((a, b) => b.val - a.val)
            .slice(0, 10);

        // Top 10 States %
        let topStatesPercent = [];
        const signupEntryStatePct = signupTopStatesPercent.find(e => e.date.startsWith(selectedDay));
        if (signupEntryStatePct && Array.isArray(signupEntryStatePct[currentMetric])) {
            topStatesPercent = signupEntryStatePct[currentMetric].map(item => ({
                id: item.state_fips,
                name: FIPS_TO_NAME[item.state_fips.toString().padStart(2, '0')] || item.state_fips,
                val: item.value,
                formattedVal: item.value.toFixed(2) + '%'
            }));
        } else {
            topStatesPercent = Object.entries(stateOuts)
                .map(([fips, val]) => {
                    // Try looking up with padded string ('06') and numeric string ('6')
                    const total = stateTotals[fips] || stateTotals[parseInt(fips)] || 1;
                    const pct = (val / total) * 100;
                    return {
                        id: fips,
                        name: FIPS_TO_NAME[fips] || fips,
                        val: pct,
                        formattedVal: pct.toFixed(2) + '%'
                    };
                })
                .sort((a, b) => b.val - a.val)
                .slice(0, 10);
        }

        // Helper for county name — works for both pro (backend countiesList) and signup
        // countiesList has { fips, name } for both tiers
        const getCountyName = (fips) => {
            const c = countiesList.find(c => c.fips.toString() === fips.toString());
            if (!c) return fips;
            const stFips = fips.toString().padStart(5, '0').substring(0, 2);
            const stName = FIPS_TO_NAME[stFips] || '';
            return stName ? `${c.name}, ${stName}` : c.name;
        };

        // Top 10 Counties Out
        let topCountiesOut = [];
        const signupEntryOut = signupTopCountiesOut.find(e => e.date.startsWith(selectedDay));
        if (signupEntryOut && Array.isArray(signupEntryOut[currentMetric])) {
            topCountiesOut = signupEntryOut[currentMetric].map(item => ({
                id: item.county_fips,
                name: getCountyName(item.county_fips),
                val: item.value,
                formattedVal: Math.round(item.value).toLocaleString()
            }));
        } else {
            topCountiesOut = Object.entries(countyOuts)
                .map(([fips, val]) => ({
                    id: fips,
                    name: getCountyName(fips),
                    val: val,
                    formattedVal: Math.round(val).toLocaleString()
                }))
                .sort((a, b) => b.val - a.val)
                .slice(0, 10);
        }

        // Top 10 Counties %
        let topCountiesPercent = [];
        const signupEntryPct = signupTopCountiesPercent.find(e => e.date.startsWith(selectedDay));
        if (signupEntryPct && Array.isArray(signupEntryPct[currentMetric])) {
            topCountiesPercent = signupEntryPct[currentMetric].map(item => ({
                id: item.county_fips,
                name: getCountyName(item.county_fips),
                val: item.value,
                formattedVal: item.value.toFixed(2) + '%'
            }));
        } else {
            topCountiesPercent = Object.entries(countyOuts)
                .map(([fips, val]) => {
                    // Try lookup with padded and unpadded
                    const total = countyTotals[fips] || countyTotals[parseInt(fips)] || 1;
                    const pct = (val / total) * 100;
                    return {
                        id: fips,
                        name: getCountyName(fips),
                        val: pct,
                        formattedVal: pct.toFixed(2) + '%'
                    };
                })
                .sort((a, b) => b.val - a.val)
                .slice(0, 10);
        }

        setProcessedStats({
            topStatesOut,
            topStatesPercent,
            topCountiesOut,
            topCountiesPercent
        });

    }, [predictionData, selectedDay, countyTotals, stateTotals, countiesList, currentMetric, signupTopCountiesOut, signupTopCountiesPercent]);

    const StatsList = ({ title, items, isPercent = false }) => (
        <div className="card shadow-sm border-secondary h-100">
            <div className="card-header bg-transparent border-secondary">
                <h6 className="mb-0 fw-bold text-main">{title}</h6>
            </div>
            <div className="card-body p-0">
                <ul className="list-group list-group-flush">
                    {items.map((item, idx) => (
                        <li key={item.id} className="list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary">
                            <span className="text-truncate" style={{ maxWidth: '65%' }} title={item.name}>
                                {idx + 1}. {item.name}
                            </span>
                            <span className={`px-2 py-0.5 rounded-pill small ${isPercent ? 'bg-info text-dark' : 'bg-primary text-white'}`} style={{ fontSize: '0.85rem' }}>
                                {item.formattedVal}
                            </span>
                        </li>
                    ))}
                    {items.length === 0 && <li className="list-group-item text-muted bg-transparent">Loading...</li>}
                </ul>
            </div>
        </div>
    );

    const statsSections = [
        {
            key: 'topStatesOut',
            shortLabel: 'States Out',
            title: 'Top 10 States (Customers Out)',
            items: processedStats.topStatesOut,
            isPercent: false
        },
        {
            key: 'topStatesPercent',
            shortLabel: 'States %',
            title: 'Top 10 States (% Out)',
            items: processedStats.topStatesPercent,
            isPercent: true
        },
        {
            key: 'topCountiesOut',
            shortLabel: 'Counties Out',
            title: 'Top 10 Counties (Customers Out)',
            items: processedStats.topCountiesOut,
            isPercent: false
        },
        {
            key: 'topCountiesPercent',
            shortLabel: 'Counties %',
            title: 'Top 10 Counties (% Out)',
            items: processedStats.topCountiesPercent,
            isPercent: true
        }
    ];

    const activeSection = statsSections.find(section => section.key === activeMobileStat) || statsSections[0];

    return (
        <div className="container-fluid mt-4 top-stats-panel">
            {isMobileViewport ? (
                <div className="top-stats-mobile">
                    <div className="top-stats-tablist" role="tablist" aria-label="Top outage rankings">
                        {statsSections.map(section => (
                            <button
                                key={section.key}
                                type="button"
                                role="tab"
                                aria-selected={activeMobileStat === section.key}
                                className={`btn ${activeMobileStat === section.key ? 'btn-secondary' : 'btn-outline-secondary'}`}
                                onClick={() => setActiveMobileStat(section.key)}
                            >
                                {section.shortLabel}
                            </button>
                        ))}
                    </div>
                    <StatsList
                        title={activeSection.title}
                        items={activeSection.items}
                        isPercent={activeSection.isPercent}
                    />
                </div>
            ) : (
                <div className="row g-3 top-stats-desktop-grid">
                    <div className="col-12 col-md-3">
                        <StatsList title="Top 10 States (Customers Out)" items={processedStats.topStatesOut} />
                    </div>
                    <div className="col-12 col-md-3">
                        <StatsList title="Top 10 States (% Out)" items={processedStats.topStatesPercent} isPercent={true} />
                    </div>
                    <div className="col-12 col-md-3">
                        <StatsList title="Top 10 Counties (Customers Out)" items={processedStats.topCountiesOut} />
                    </div>
                    <div className="col-12 col-md-3">
                        <StatsList title="Top 10 Counties (% Out)" items={processedStats.topCountiesPercent} isPercent={true} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default TopStatsPanel;
